<template>
  <div class="card">
    <div class="card-header bg-success text-white">
      <h5 class="mb-0">
        <i class="fas fa-check-circle mr-2"></i>
        Solicitudes Autorizadas
      </h5>
    </div>
    <div class="card-body">
      <!-- Date Range Filter -->
      <div class="row mb-4">
        <div class="col-md-4">
          <div class="color-primary-4 font-14 font-weight-bold mb-1">
            Desde:
          </div>
          <custom-date-picker
            v-model="filters.dateFrom"
            class="input__extended"
            placeholder="DD/MM/YYYY"
            class-container="w-100"
            @input="onDateChange"
          />
        </div>
        <div class="col-md-4">
          <div class="color-primary-4 font-14 font-weight-bold mb-1">
            Hasta:
          </div>
          <custom-date-picker
            v-model="filters.dateTo"
            class="input__extended"
            placeholder="DD/MM/YYYY"
            class-container="w-100"
            @input="onDateChange"
          />
        </div>
        <div class="col-md-4 d-flex align-items-end">
          <action-button
            class="btn-primary px-4 py-2 mr-2"
            text="Buscar"
            i="fa fa-search"
            @click="onSearchHandler"
          />
          <action-button
            class="btn-success px-4 py-2"
            text="Exportar a Excel"
            i="fa fa-file-excel"
            @click="onExportHandler"
            :disabled="!hasData"
          />
        </div>
      </div>

      <!-- Data Table -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Cargando...</span>
        </div>
      </div>

      <div v-else-if="hasData" class="table-responsive">
        <table class="table table-striped table-hover">
          <thead class="thead-dark">
            <tr>
              <th>Título</th>
              <th>Investigador</th>
              <th>Correo Electrónico</th>
              <th>Teléfono</th>
              <th>Número de Solicitud</th>
              <th>Universidad</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in reportData" :key="index">
              <td>{{ item.titulo || '-' }}</td>
              <td>{{ item.investigador || '-' }}</td>
              <td>{{ item.correoElectronico || '-' }}</td>
              <td>{{ item.numeroTelefonico || '-' }}</td>
              <td>{{ item.numeroSolicitud || '-' }}</td>
              <td>{{ item.universidadODondeProviene || '-' }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Results Summary -->
        <div class="text-muted font-14 mt-3">
          Total de Resultados: {{ reportData.length }}
        </div>
      </div>

      <div v-else class="text-center py-5">
        <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
        <p class="text-muted">
          {{
            searched
              ? 'No se encontraron resultados'
              : 'Seleccione un rango de fechas para buscar'
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
//libs
import ActionButton from 'ActionButton';
import CustomDatePicker from 'custom-date-picker';

//mixins
import loadingMixin from '@quisit/mipe-utils/mixins/loading';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';

//services
import {
  getAuthorizedRequestsReport,
  exportAuthorizedRequestsToExcel,
} from '@/services/report.api.js';

//utils
import { i18n } from '@/i18n.js';

export default {
  name: 'AuthorizedRequestsReport',
  mixins: [loadingMixin, alertMixin],
  components: {
    ActionButton,
    CustomDatePicker,
  },
  data: () => ({
    filters: {
      dateFrom: null,
      dateTo: null,
    },
    reportData: [],
    loading: false,
    searched: false,
  }),
  computed: {
    hasData() {
      return this.reportData && this.reportData.length > 0;
    },
  },
  methods: {
    onDateChange() {
      // Reset searched state when dates change
      this.searched = false;
    },
    async onSearchHandler() {
      if (!this.filters.dateFrom || !this.filters.dateTo) {
        this.ShowToast(
          i18n.t('Alert'),
          i18n.t('PleaseSelectDateRange'),
          'warning'
        );
        return;
      }

      // Validate date range
      const fromDate = new Date(this.filters.dateFrom);
      const toDate = new Date(this.filters.dateTo);

      if (fromDate > toDate) {
        this.ShowToast(i18n.t('Alert'), i18n.t('InvalidDateRange'), 'warning');
        return;
      }

      try {
        this.loading = true;
        this.searched = true;

        const { data } = await getAuthorizedRequestsReport(
          this.formatDate(this.filters.dateFrom),
          this.formatDate(this.filters.dateTo)
        );

        this.reportData = data || [];
        this.loading = false;

        if (!this.hasData) {
          this.ShowToast(i18n.t('Info'), i18n.t('NoResultsFound'), 'info');
        }
      } catch (error) {
        this.loading = false;
        this.ShowToast(i18n.t('Error'), i18n.t('ErrorFetchingData'), 'error');
        console.error('Error fetching authorized requests report:', error);
      }
    },
    async onExportHandler() {
      if (!this.hasData) {
        return;
      }

      try {
        this.ShowWait();

        const response = await exportAuthorizedRequestsToExcel(
          this.formatDate(this.filters.dateFrom),
          this.formatDate(this.filters.dateTo)
        );

        // Create blob from response
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Solicitudes_Autorizadas_${this.formatDate(
          this.filters.dateFrom
        )}_${this.formatDate(this.filters.dateTo)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.HideWait();
        this.ShowToast(
          i18n.t('Success'),
          i18n.t('ExportedSuccessfully'),
          'success'
        );
      } catch (error) {
        this.HideWait();
        this.ShowToast(i18n.t('Error'), i18n.t('ErrorExportingData'), 'error');
        console.error('Error exporting to Excel:', error);
      }
    },
    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/bootstrap/_colors.scss';

.table {
  font-size: 14px;

  th {
    white-space: nowrap;
    font-weight: 600;
  }

  td {
    vertical-align: middle;
  }
}

.input__extended {
  ::v-deep .form-control {
    height: 38px;
  }
}
</style>
